<script setup lang="ts">
const colorMode = useColorMode();

const emit = defineEmits<{ 'cta-clicked': []; }>();

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    ctaText: string;
    ctaLink?: string | null;
    imageDarkSrc: string;
    imageLightSrc: string;
    imageMarginRight?: string;
    imageMarginTop?: string;
    imageMarginLeft?: string;
  }>(),
  {
    ctaText: "Let's Craft Yours",
    ctaLink: null,
    imageDarkSrc: '/images/bigcard_graphic_Dark.svg',
    imageLightSrc: '/images/bigcard_graphic_Light.svg',
    imageMarginRight: '0px',
    imageMarginTop: '-16px',
    imageMarginLeft: '0px',
  }
);
</script>

<template>
  <div class="relative z-[1] flex justify-center mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]">
    <div
      class="relative overflow-hidden rounded-[8px] h-auto lg:h-[362px]"
      :style="{
        width: '100%',
        maxWidth: '964px',
        border: colorMode.preference === 'light' ? '1px solid rgba(192,108,253,0.5)' : '1px solid #3D1B60',
        background: colorMode.preference === 'light' ? '#FFFFFF' : 'linear-gradient(32deg, rgba(1,3,20,0.40) -1.26%, rgba(43,1,82,0.40) 100.92%)'
      }"
    >
      <!-- Decorative horizontal lines SVG -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="964"
        height="362"
        viewBox="0 0 964 362"
        fill="none"
        class="absolute top-0 left-0 w-full h-full pointer-events-none"
      >
        <path v-for="(_, i) in 73" :key="i"
          :d="`M0 ${i * 6.06354}H1033.67V${i * 6.06354 + 2.42541}H0V${i * 6.06354}Z`"
          fill="url(#wc_paint0_linear)"
          fill-opacity="0.03"
        />
        <defs>
          <linearGradient id="wc_paint0_linear" x1="516.834" y1="0" x2="516.834" y2="439" gradientUnits="userSpaceOnUse">
            <stop stop-color="#696FD0" />
            <stop offset="1" stop-color="#5359C6" stop-opacity="0" />
          </linearGradient>
        </defs>
      </svg>

      <!-- Blurred circle glow - bottom left -->
      <div
        class="absolute opacity-[0.36] pointer-events-none"
        :style="{
          bottom: '-230px',
          left: '-300px',
          width: '500px',
          height: '600px',
          borderRadius: '500px',
          background: colorMode.preference === 'light' ? 'linear-gradient(180deg, rgba(161,37,243,0.09) 41.7%, rgba(137,22,212,0.00) 88.71%)' : 'linear-gradient(180deg, #A125F3 41.7%, rgba(137,22,212,0.00) 88.71%)',
          filter: 'blur(99.0999984741211px)'
        }"
      />

      <!-- Blurred circle glow - top right -->
      <div
        class="absolute opacity-[0.36] pointer-events-none"
        :style="{
          top: '-200px',
          right: '-350px',
          width: '429.438px',
          height: '429.438px',
          borderRadius: '429.438px',
          background: colorMode.preference === 'light' ? 'linear-gradient(180deg, rgba(161,37,243,0.26) 41.7%, rgba(137,22,212,0.00) 88.71%)' : 'linear-gradient(180deg, #A125F3 41.7%, rgba(137,22,212,0.00) 88.71%)',
          filter: 'blur(99.0999984741211px)'
        }"
      />

      <!-- Content -->
      <div class="relative z-[1] h-full flex flex-col lg:flex-row items-center">
        <div class="flex flex-col justify-start h-full min-w-0 pt-10 lg:pt-11 pr-6 pb-8 lg:pb-12 pl-6 lg:pl-12 items-center lg:items-start flex-1">
          <h2
            class="text-center lg:text-left text-[24px] lg:text-[40px] font-semibold leading-tight"
            style="max-width: 100%; font-family: 'ITC Avant Garde Gothic', 'AvantGarde', sans-serif; background: linear-gradient(92deg, #BA55FE -3.57%, #12DBFF 80.72%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent;"
          >
            {{ heading }}
          </h2>
          <p
            class="text-center lg:text-left font-normal mt-2"
            style="font-family: 'ITC Avant Garde Gothic', 'AvantGarde', sans-serif; font-size: clamp(14px, 2vw, 18px); letter-spacing: 0.36px;"
            :class="colorMode.preference === 'dark' ? 'text-[#D9D9D9]' : 'text-black'"
          >
            {{ subcopy }}
          </p>

          <!-- Desktop CTA -->
          <div class="text-left mt-[45px] hidden lg:block">
            <WrButton :link="ctaLink || undefined" @click="!ctaLink ? emit('cta-clicked') : undefined" container-class="h-[53px]" append-class="px-[22px] text-[15px] rounded-[56px]">{{ ctaText }}</WrButton>
          </div>
        </div>

        <img
          loading="lazy"
          :src="colorMode.preference === 'light' ? props.imageLightSrc : props.imageDarkSrc"
          alt="why choose us image"
          class="flex-shrink-0 w-full h-auto lg:w-auto lg:h-[87%] object-contain -mt-4"
          :style="{ marginRight: props.imageMarginRight, marginTop: props.imageMarginTop, marginLeft: props.imageMarginLeft }"
        />

        <!-- Mobile CTA - below the image -->
        <div class="w-full px-6 pb-10 lg:hidden flex justify-center">
          <WrButton :link="ctaLink || undefined" @click="!ctaLink ? emit('cta-clicked') : undefined" container-class="h-[45px]" append-class="px-5 text-[13px] rounded-[56px]">{{ ctaText }}</WrButton>
        </div>
      </div>
    </div>
  </div>
</template>
